import { LANGUAGE } from "services/general";
import { HAS_PERMISSION } from "services/user";
import { PAYABLE_TYPES } from "constants/general";
import {
  invoiceRecordType,
  RECORD_TYPE,
  recordUrl,
} from "constants/recordLinks";
import {
  CUSTOMERS_INDEX,
  ENTITIES_INDEX,
  SALES_INVOICES_INDEX,
  PROJECTS_INDEX,
  PROPERTIES_INDEX,
  UNITS_INDEX,
  SANADAT_INDEX,
  LEADS_CONTACT_INDEX,
  CONTRACTS_INDEX,
  INVENTORIES_INDEX,
  SUPPORT_CENTER_TICKETS_INDEX,
  UNIT_MESSAGES_INDEX,
} from "permissions";

// Module keys
export const CATEGORIES = {
  ALL: "all",
  PROJECTS: "projects",
  PROPERTIES: "properties",
  UNITS: "units",
  INVOICES: "invoices",
  CUSTOMERS: "customers",
  ENTITIES: "entities",
  VOUCHERS: "vouchers",
  LEAD_CONTACTS: "lead_contacts",
  CONTRACTS: "contracts",
  PRODUCTS: "products",
  SUPPORT_CENTER: "support_center",
  UNIT_MESSAGES: "unit_messages",
};

/** Map invoice search result → detail route (matches routes.js invoice paths). */
const getInvoiceRoute = (item) => {
  const id = item?.id;
  if (!id) return `/${LANGUAGE}/sales-invoices`;

  return recordUrl(invoiceRecordType(item.type), id);
};

/** Map contract search result → rent/sales profile route (matches routes.js). */
const getContractRoute = (item) => {
  const id = item?.id;
  if (!id) return `/${LANGUAGE}/contracts`;

  const type = item.type || item.contract_type;
  const isSale =
    type === PAYABLE_TYPES.SALE_CONTRACT ||
    type === "sell" ||
    type === "sale" ||
    type === "sales";

  return recordUrl(
    isSale ? RECORD_TYPE.SELL_CONTRACT : RECORD_TYPE.RENT_CONTRACT,
    id
  );
};

// Module configuration - single source of truth
export const SEARCH_MODULES = {
  [CATEGORIES.CUSTOMERS]: {
    key: CATEGORIES.CUSTOMERS,
    permissionId: CUSTOMERS_INDEX,
    endpoint: "dashboard/customers-search",
    // Field mappings for display - these will be used to extract title/subtitle from API response
    fieldMappings: {
      title: "full_name", // Changed from "name" to "full_name"
      subtitle: "mobile", // Changed from "type" to "mobile"
      amount: null, // Optional amount field
    },
    // Route pattern for navigation when result is clicked
    getRoute: (item) => recordUrl(RECORD_TYPE.CUSTOMER, item.id),
  },
  [CATEGORIES.ENTITIES]: {
    key: CATEGORIES.ENTITIES,
    permissionId: ENTITIES_INDEX,
    endpoint: "dashboard/entities-search",
    fieldMappings: {
      title: "name",
      subtitle: null,
    },
    // Entities have no detail route — open the list page
    getRoute: () => `/${LANGUAGE}/entities`,
  },
  [CATEGORIES.INVOICES]: {
    key: CATEGORIES.INVOICES,
    permissionId: SALES_INVOICES_INDEX,
    endpoint: "dashboard/invoices-search",
    fieldMappings: {
      title: "serial", // Serial number (e.g., "INV-27")
      subtitle: "name", // Customer name
      amount: "amount", // Invoice amount
    },
    getRoute: getInvoiceRoute,
  },
  [CATEGORIES.PROJECTS]: {
    key: CATEGORIES.PROJECTS,
    permissionId: PROJECTS_INDEX,
    endpoint: "dashboard/projects-search",
    fieldMappings: {
      title: "title", // Changed from "name" to "title"
      subtitle: "status", // e.g., "completed", "under_construction", "paused"
      amount: null,
    },
    getRoute: (item) => recordUrl(RECORD_TYPE.PROJECT, item.id),
  },
  [CATEGORIES.PROPERTIES]: {
    key: CATEGORIES.PROPERTIES,
    permissionId: PROPERTIES_INDEX,
    endpoint: "dashboard/properties-search",
    fieldMappings: {
      title: "name",
      subtitle: "location", // e.g., "حي الملقا - 120 وحدة"
      amount: null,
    },
    getRoute: (item) => recordUrl(RECORD_TYPE.PROPERTY, item.id),
  },
  [CATEGORIES.UNITS]: {
    key: CATEGORIES.UNITS,
    permissionId: UNITS_INDEX,
    endpoint: "dashboard/units-search",
    fieldMappings: {
      title: "name", // e.g., "شقة رقم 302"
      subtitle: "property.name", // Changed to nested "property.name"
      amount: "price.price", // Changed to nested "price.price"
    },
    getRoute: (item) => recordUrl(RECORD_TYPE.UNIT, item.id),
  },
  [CATEGORIES.VOUCHERS]: {
    key: CATEGORIES.VOUCHERS,
    permissionId: SANADAT_INDEX,
    endpoint: "dashboard/vouchers-search",
    fieldMappings: {
      title: "serial", // Changed from "voucher_number" to "serial"
      subtitle: "voucherable_name", // Changed from "entity_name" to "voucherable_name"
      amount: "amount", // Voucher amount
    },
    getRoute: (item) => recordUrl(RECORD_TYPE.VOUCHER, item.id),
  },
  [CATEGORIES.LEAD_CONTACTS]: {
    key: CATEGORIES.LEAD_CONTACTS,
    permissionId: LEADS_CONTACT_INDEX,
    endpoint: "dashboard/lead_contacts/search",
    fieldMappings: {
      title: "full_name",
      subtitle: "phone_number", // Can also use "email"
      amount: null,
    },
    // Contacts have no detail route — open the contacts list
    getRoute: () => `/${LANGUAGE}/crm/contacts`,
  },
  [CATEGORIES.CONTRACTS]: {
    key: CATEGORIES.CONTRACTS,
    permissionId: CONTRACTS_INDEX,
    endpoint: "dashboard/contracts-search",
    fieldMappings: {
      title: "id", // Contract ID
      subtitle: "customer.full_name", // Nested customer name
      amount: null,
    },
    getRoute: getContractRoute,
  },
  [CATEGORIES.PRODUCTS]: {
    key: CATEGORIES.PRODUCTS,
    permissionId: INVENTORIES_INDEX,
    endpoint: "dashboard/inventories-search",
    fieldMappings: {
      title: "title", // API response shows "title" field
      subtitle: null,
      amount: null,
    },
    getRoute: (item) => recordUrl(RECORD_TYPE.PRODUCT, item.id),
  },
  [CATEGORIES.SUPPORT_CENTER]: {
    key: CATEGORIES.SUPPORT_CENTER,
    permissionId: SUPPORT_CENTER_TICKETS_INDEX,
    endpoint: "dashboard/support-center-tickets",
    fieldMappings: {
      title: "subject",
      subtitle: null, // built from customer in normalizeSearchResult
      amount: null,
    },
    getRoute: (item) => recordUrl(RECORD_TYPE.SUPPORT_TICKET, item.id),
  },
  [CATEGORIES.UNIT_MESSAGES]: {
    key: CATEGORIES.UNIT_MESSAGES,
    permissionId: UNIT_MESSAGES_INDEX,
    endpoint: "dashboard/unit-messages/conversations",
    fieldMappings: {
      title: "partner.full_name",
      subtitle: "created_at",
      amount: null,
    },
    getRoute: (item) => {
      const conversationId = item.conversation_id || item.id;
      return (
        recordUrl(RECORD_TYPE.UNIT_CONVERSATION, conversationId) ??
        `/${LANGUAGE}/units-messages`
      );
    },
  },
};

// Helper function to get nested property value (e.g., "property.name" or "price.price")
const getNestedValue = (obj, path) => {
  if (!path) return null;
  return path.split(".").reduce((current, prop) => current?.[prop], obj);
};

// Helper function to normalize API response to consistent format
export const normalizeSearchResult = (item, moduleKey) => {
  const module = SEARCH_MODULES[moduleKey];
  if (!module) return item;

  const { fieldMappings } = module;

  // Get title value - try nested path first, then direct property, then fallback
  const titleValue = fieldMappings.title
    ? getNestedValue(item, fieldMappings.title) ||
      item[fieldMappings.title] ||
      item.name ||
      item.title ||
      ""
    : item.name || item.title || "";

  // Get subtitle value - try nested path first, then direct property, then fallback
  const subtitleValue = fieldMappings.subtitle
    ? getNestedValue(item, fieldMappings.subtitle) ||
      item[fieldMappings.subtitle] ||
      item.subtitle ||
      ""
    : item.subtitle || "";

  const normalized = {
    id: item.id,
    name: titleValue,
    subtitle: subtitleValue,
  };

  // Add amount if field mapping exists and item has that field
  if (fieldMappings.amount) {
    const amountValue =
      getNestedValue(item, fieldMappings.amount) || item[fieldMappings.amount];
    if (
      amountValue !== null &&
      amountValue !== undefined &&
      amountValue !== ""
    ) {
      normalized.amount = amountValue;
    }
  }

  // Add type field for customers (e.g., "individual", "company")
  if (moduleKey === CATEGORIES.CUSTOMERS && item.type) {
    normalized.type = item.type;
  }

  // Add type field for entities (e.g., "supplier", "organization", "utility")
  if (moduleKey === CATEGORIES.ENTITIES && item.type) {
    normalized.type = item.type;
  }

  // Add project fields for properties (project_name, project_en_name, project_status)
  if (moduleKey === CATEGORIES.PROPERTIES) {
    if (item.project_name || item.project_en_name) {
      normalized.project_name = item.project_name;
      normalized.project_en_name = item.project_en_name;
    }
    if (item.project_status) {
      normalized.project_status = item.project_status;
    }
  }

  // Add district and total_units fields for projects
  if (moduleKey === CATEGORIES.PROJECTS) {
    if (item.district_name || item.en_district_name) {
      normalized.district_name = item.district_name;
      normalized.en_district_name = item.en_district_name;
    }
    if (item.total_units !== null && item.total_units !== undefined) {
      normalized.total_units = item.total_units;
    }
  }

  // Add type field for vouchers (e.g., "receipt", "payment")
  if (moduleKey === CATEGORIES.VOUCHERS && item.type) {
    normalized.type = item.type;
  }

  // Preserve type for invoice/contract route resolution
  if (
    (moduleKey === CATEGORIES.INVOICES ||
      moduleKey === CATEGORIES.CONTRACTS) &&
    item.type
  ) {
    normalized.type = item.type;
  }

  // Format contract ID for display (e.g., "Contract #45")
  if (moduleKey === CATEGORIES.CONTRACTS && item.id) {
    normalized.name = `Contract #${item.id}`;
  }

  // Support Center: subtitle = customer name (fname + lname)
  if (moduleKey === CATEGORIES.SUPPORT_CENTER && item.customer) {
    const c = item.customer;
    normalized.subtitle = [c.fname, c.lname].filter(Boolean).join(" ") || "";
  }

  // Preserve original item data for any additional fields needed
  normalized.original = item;

  return normalized;
};

// Get all module keys except ALL
export const getAllModuleKeys = () => {
  return Object.keys(SEARCH_MODULES);
};

/**
 * Returns only the module keys the current user has permission to access.
 * Used to gate both the category tabs UI and the search API calls.
 */
export const getPermittedModuleKeys = () => {
  return Object.keys(SEARCH_MODULES).filter((key) => {
    const mod = SEARCH_MODULES[key];
    // If no permissionId is defined, allow by default
    if (mod.permissionId === undefined || mod.permissionId === null) return true;
    return HAS_PERMISSION(mod.permissionId);
  });
};
